import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    ArrowLeftIcon,
    PlusIcon,
    XMarkIcon,
    ChevronDownIcon
} from '@heroicons/react/24/outline';

const AdminProductEdit = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);

    // SEO Preview State
    const [seoTitle, setSeoTitle] = useState('');
    const [seoDesc, setSeoDesc] = useState('');

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        status: 'active',
        images: [],
        price_b2c: '',
        price_b2b: '', // Custom field
        cost_price: '',
        sku: '',
        barcode: '',
        track_quantity: true,
        stock_b2c: 0,
        stock_b2b: 0, // Custom field
        min_stock_level: 10,
        weight: '',
        category_id: '',
        brand_id: '',
        tags: [],
        is_featured: false,
        fragrance_family: '',
        occasion: '',
        intensity: 3,
        // Variants mockup
        has_variants: false,
        options: []
    });

    const [newTag, setNewTag] = useState('');

    useEffect(() => {
        fetchData();
    }, [productId]);

    useEffect(() => {
        // Update SEO preview when name/desc changes if not manually set
        if (!seoTitle) setSeoTitle(formData.name);
        if (!seoDesc) setSeoDesc(formData.description?.substring(0, 160) || '');
    }, [formData.name, formData.description]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [productRes, categoriesRes, brandsRes] = await Promise.all([
                axios.get(`/api/products/${productId}`),
                axios.get('/api/categories'),
                axios.get('/api/brands')
            ]);

            const product = productRes.data.product || productRes.data;
            setCategories(categoriesRes.data.categories || []);
            setBrands(brandsRes.data.brands || brandsRes.data || []);

            // STOCK MIGRATION LOGIC:
            // If total stock > 0 but both warehouses are 0, this is legacy data.
            // Move it all to B2C by default so the user sees it.
            let b2cStock = product.stock_b2c || 0;
            const b2bStock = product.stock_b2b || 0;
            const totalStock = product.stock_quantity || 0;

            if (totalStock > 0 && b2cStock === 0 && b2bStock === 0) {
                b2cStock = totalStock;
                // We will dirty the form immediately if this happens? 
                // Maybe better to just show it as initial state, and since we are PUTting everything on save, it will persist.
            }

            setFormData({
                name: product.name || '',
                description: product.description || '',
                status: product.is_active ? 'active' : 'draft',
                images: product.images || [],
                price_b2c: product.price_b2c || '',
                price_b2b: product.price_b2b || '',
                cost_price: product.cost_price || '',
                sku: product.sku || '',
                barcode: product.barcode || '',
                track_quantity: product.track_quantity,
                stock_b2c: b2cStock,
                stock_b2b: b2bStock,
                min_stock_level: product.min_stock_level || 10,
                weight: product.weight || '',
                category_id: product.category_id || '',
                brand_id: product.brand_id || '',
                tags: product.tags || [],
                is_featured: product.is_featured || false,
                fragrance_family: product.fragrance_family || '',
                occasion: product.occasion || '',
                intensity: product.intensity || 3,
                has_variants: false, // Mock
                options: []
            });

            // Initial state is not dirty
            setTimeout(() => setIsDirty(false), 100);
        } catch (error) {
            toast.error('שגיאה בטעינת הנתונים');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setIsDirty(true);
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter' && newTag.trim()) {
            e.preventDefault();
            if (!formData.tags.includes(newTag.trim())) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...prev.tags, newTag.trim()]
                }));
                setIsDirty(true);
            }
            setNewTag('');
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
        setIsDirty(true);
    };

    const fileInputRef = useRef(null);

    const addImageUrl = () => {
        const url = prompt('הכנס כתובת תמונה:');
        if (url) {
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, url]
            }));
            setIsDirty(true);
        }
    };

    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const uploadData = new FormData();
        files.forEach(file => {
            uploadData.append('images', file);
        });

        try {
            setLoading(true);
            const response = await axios.post('/api/upload/products', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.urls) {
                setFormData(prev => ({
                    ...prev,
                    images: [...prev.images, ...response.data.urls]
                }));
                setIsDirty(true);
                toast.success('התמונות הועלו בהצלחה');
            }
        } catch (error) {
            toast.error('שגיאה בהעלאת התמונות');
        } finally {
            setLoading(false);
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removeImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
        setIsDirty(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await axios.put(`/api/products/${productId}`, {
                ...formData,
                is_active: formData.status === 'active'
            });
            toast.success('המוצר נשמר');
            setIsDirty(false);
        } catch (error) {
            toast.error('שגיאה בשמירה');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-[#f1f2f3]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div></div>;
    }

    return (
        <div className="min-h-screen bg-[#f1f2f3] pb-24 font-sans text-[#202223]" dir="rtl">

            {/* Contextual Save Bar - Only Visible when Dirty */}
            <div
                className={`fixed top-0 left-0 right-0 bg-black text-white px-6 py-3 z-50 flex items-center justify-between transition-transform duration-300 transform ${isDirty ? 'translate-y-0' : '-translate-y-full'}`}
            >
                <div className="flex items-center gap-4">
                    <span className="text-sm font-medium">שינויים שלא נשמרו</span>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => {
                            if (window.confirm('האם לבטל את השינויים?')) {
                                fetchData();
                                setIsDirty(false);
                            }
                        }}
                        className="px-4 py-1.5 rounded bg-transparent hover:bg-white/10 text-sm font-medium text-gray-200 transition-colors"
                    >
                        ביטול
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`px-4 py-1.5 rounded bg-[#008060] hover:bg-[#006e52] text-sm font-medium transition-all shadow-sm ${saving ? 'opacity-70' : ''}`}
                    >
                        {saving ? 'שומר...' : 'שמור'}
                    </button>
                </div>
            </div>

            {/* Main Header */}
            <div className="max-w-[1000px] mx-auto px-4 pt-8 pb-6">
                <div className="flex items-center gap-3 mb-4">
                    <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-200 rounded text-gray-500">
                        <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <h1 className="text-xl font-bold text-[#202223]">{formData.name}</h1>
                    <div className={`px-2 py-0.5 text-xs font-medium rounded-full ${formData.status === 'active' ? 'bg-[#aee9d1] text-[#004e30]' : 'bg-[#e4e5e7] text-[#424d57]'}`}>
                        {formData.status === 'active' ? 'פעיל' : 'טיוטה'}
                    </div>
                </div>
            </div>

            <div className="max-w-[1000px] mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-5">

                {/* Left Column (Main) */}
                <div className="lg:col-span-2 space-y-5">

                    {/* Title & Description Card */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <div className="space-y-4">
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">כותרת</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                />
                            </div>
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">תיאור</label>
                                <textarea
                                    name="description"
                                    rows="8"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Media Card */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <div className="flex items-center justify-between mb-4">
                            <label className="text-[13px] font-semibold text-[#202223]">מדיה</label>
                            <button
                                type="button"
                                onClick={addImageUrl}
                                className="text-[13px] text-[#005bd3] hover:underline"
                            >
                                הוסף מ-URL
                            </button>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {formData.images.length > 0 && formData.images.map((img, idx) => (
                                <div key={idx} className="group relative aspect-square bg-white rounded border border-[#e1e3e5] overflow-hidden hover:border-[#898f94] transition-colors">
                                    <img src={img.startsWith('http') ? img : `/api${img}`} alt="" className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors" />
                                    <button
                                        onClick={() => removeImage(idx)}
                                        className="absolute top-1 right-1 p-1 bg-white rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100"
                                    >
                                        <XMarkIcon className="h-4 w-4 text-gray-600" />
                                    </button>
                                </div>
                            ))}

                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className={`aspect-square flex flex-col items-center justify-center border border-dashed border-[#898f94] rounded hover:bg-[#fafbfb] cursor-pointer transition-colors ${formData.images.length === 0 ? 'col-span-2 sm:col-span-4 h-40' : ''}`}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    className="hidden"
                                    multiple
                                    accept="image/*"
                                    onChange={handleFileUpload}
                                />
                                <div className="p-2 mb-2">
                                    <PlusIcon className="h-5 w-5 text-[#5c5f62]" />
                                </div>
                                <span className="text-[13px] text-[#5c5f62] font-medium">הוסף קבצים</span>
                                <span className="text-xs text-[#6d7175]">או גרור ושחרר</span>
                            </div>
                        </div>
                    </div>

                    {/* Pricing Card */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <h3 className="text-[13px] font-semibold text-[#202223] mb-4">תמחור</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">מחיר B2C (צרכן)</label>
                                <div className="relative">
                                    <span className="absolute right-3 top-2 text-[#6d7175]">₪</span>
                                    <input
                                        type="number"
                                        name="price_b2c"
                                        placeholder="0.00"
                                        value={formData.price_b2c}
                                        onChange={handleInputChange}
                                        className="w-full pr-7 pl-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">מחיר B2B (סיטונאי)</label>
                                <div className="relative">
                                    <span className="absolute right-3 top-2 text-[#6d7175]">₪</span>
                                    <input
                                        type="number"
                                        name="price_b2b"
                                        placeholder="0.00"
                                        value={formData.price_b2b}
                                        onChange={handleInputChange}
                                        className="w-full pr-7 pl-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-[#e1e3e5] my-4"></div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">מחיר ליחידה (עלות)</label>
                                <div className="relative">
                                    <span className="absolute right-3 top-2 text-[#6d7175]">₪</span>
                                    <input
                                        type="number"
                                        name="cost_price"
                                        placeholder="0.00"
                                        value={formData.cost_price}
                                        onChange={handleInputChange}
                                        className="w-full pr-7 pl-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                    />
                                </div>
                                <p className="text-xs text-[#6d7175] mt-1">לקוחות לא יראו את זה</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4 items-center pt-5">
                                <div>
                                    <span className="block text-xs text-[#6d7175] mb-1">מרווח (Margin)</span>
                                    <span className="block text-[13px] text-[#202223]">
                                        {(formData.price_b2c && formData.cost_price && parseFloat(formData.price_b2c) > 0)
                                            ? `${(((parseFloat(formData.price_b2c) - parseFloat(formData.cost_price)) / parseFloat(formData.price_b2c)) * 100).toFixed(0)}%`
                                            : '-'
                                        }
                                    </span>
                                </div>
                                <div>
                                    <span className="block text-xs text-[#6d7175] mb-1">רווח</span>
                                    <span className="block text-[13px] text-[#202223]">
                                        {(formData.price_b2c && formData.cost_price)
                                            ? `₪${(parseFloat(formData.price_b2c) - parseFloat(formData.cost_price)).toFixed(2)}`
                                            : '-'
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Inventory Card */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <h3 className="text-[13px] font-semibold text-[#202223] mb-4">מלאי</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">מק״ט (SKU)</label>
                                <input
                                    type="text"
                                    name="sku"
                                    value={formData.sku}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                />
                            </div>
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">ברקוד (ISBN, UPC, GTIN)</label>
                                <input
                                    type="text"
                                    name="barcode"
                                    value={formData.barcode}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <div className="flex items-start gap-3">
                                <div className="flex h-5 items-center">
                                    <input
                                        type="checkbox"
                                        name="track_quantity"
                                        checked={formData.track_quantity}
                                        onChange={handleInputChange}
                                        className="h-4 w-4 rounded border-gray-300 text-[#008060] focus:ring-[#008060]"
                                    />
                                </div>
                                <div className="text-sm">
                                    <label className="font-medium text-[#202223]">עקוב אחר כמות</label>
                                </div>
                            </div>
                        </div>

                        {formData.track_quantity && (
                            <div className="border-t border-[#e1e3e5] pt-4 mt-4">
                                <h4 className="text-[13px] font-semibold text-[#202223] mb-3">כמות</h4>
                                <div className="py-3 flex justify-between items-center border-b border-[#e1e3e5] last:border-0">
                                    <div>
                                        <p className="text-[13px] text-[#202223]">B2C Warehouse</p>
                                        <p className="text-xs text-[#6d7175]">זמין</p>
                                    </div>
                                    <input
                                        type="number"
                                        name="stock_b2c"
                                        value={formData.stock_b2c}
                                        onChange={handleInputChange}
                                        className="w-24 px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] outline-none text-left"
                                    />
                                </div>
                                <div className="py-3 flex justify-between items-center border-b border-[#e1e3e5] last:border-0">
                                    <div>
                                        <p className="text-[13px] text-[#202223]">B2B Warehouse</p>
                                        <p className="text-xs text-[#6d7175]">זמין</p>
                                    </div>
                                    <input
                                        type="number"
                                        name="stock_b2b"
                                        value={formData.stock_b2b}
                                        onChange={handleInputChange}
                                        className="w-24 px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] outline-none text-left"
                                    />
                                </div>
                                <div className="py-3 flex justify-between items-center">
                                    <div>
                                        <p className="text-[13px] font-semibold text-[#202223]">סה״כ מלאי</p>
                                        <p className="text-xs text-[#6d7175]">מחושב אוטומטית</p>
                                    </div>
                                    <div className="w-24 px-3 py-2 bg-gray-50 border border-[#e1e3e5] rounded text-left font-semibold text-[#202223]">
                                        {parseInt(formData.stock_b2c || 0) + parseInt(formData.stock_b2b || 0)}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Shipping Card */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <h3 className="text-[13px] font-semibold text-[#202223] mb-4">משלוח</h3>
                        <div>
                            <label className="block text-[13px] text-[#202223] mb-1">משקל</label>
                            <div className="flex gap-2 w-full md:w-1/2">
                                <input
                                    type="number"
                                    step="0.001"
                                    name="weight"
                                    value={formData.weight}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                />
                                <select className="px-3 border border-[#898f94] rounded bg-white text-[#202223] outline-none">
                                    <option>kg</option>
                                    <option>g</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Variants Card (Placeholder) */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <h3 className="text-[13px] font-semibold text-[#202223] mb-4">אפשרויות</h3>
                        <div className="flex items-start gap-3">
                            <div className="flex h-5 items-center">
                                <input
                                    type="checkbox"
                                    disabled
                                    className="h-4 w-4 rounded border-gray-300 text-[#008060]"
                                />
                            </div>
                            <div className="text-sm">
                                <label className="font-medium text-[#202223] text-gray-400">למוצר זה יש אפשרויות, כגון גודל או צבע (בקרוב)</label>
                            </div>
                        </div>
                    </div>

                    {/* SEO Preview Card */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-[13px] font-semibold text-[#202223]">תצוגה מקדימה ברישומי מנוע חיפוש</h3>
                            <button className="text-[13px] text-[#005bd3] hover:underline">עריכה</button>
                        </div>
                        <div className="space-y-1">
                            <p className="text-[18px] text-[#1a0dab] hover:underline cursor-pointer">{seoTitle || 'שם המוצר'}</p>
                            <p className="text-[13px] text-[#006621] dir-ltr text-right">https://lumel.com/products/{productId}</p>
                            <p className="text-[13px] text-[#545454]">{seoDesc || 'תיאור המוצר יופיע כאן...'}</p>
                        </div>
                    </div>

                </div>

                {/* Right Column (Sidebar) */}
                <div className="space-y-5">

                    {/* Status Card */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <h3 className="text-[13px] font-semibold text-[#202223] mb-4">סטטוס מוצר</h3>
                        <div className="relative">
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full appearance-none px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none bg-white pr-8 z-10 relative"
                            >
                                <option value="active">פעיל</option>
                                <option value="draft">טיוטה</option>
                            </select>
                            <ChevronDownIcon className="h-4 w-4 text-[#5c5f62] absolute left-3 top-3 z-0" />
                        </div>
                    </div>

                    {/* Organization Card */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <h3 className="text-[13px] font-semibold text-[#202223] mb-4">ארגון מוצר</h3>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">קטגוריה</label>
                                <div className="relative">
                                    <select
                                        name="category_id"
                                        value={formData.category_id}
                                        onChange={handleInputChange}
                                        className="w-full appearance-none px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none bg-white pr-8"
                                    >
                                        <option value="">בחר קטגוריה</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="h-4 w-4 text-[#5c5f62] absolute left-3 top-3" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">ספק (מותג)</label>
                                <div className="relative">
                                    <select
                                        name="brand_id"
                                        value={formData.brand_id}
                                        onChange={handleInputChange}
                                        className="w-full appearance-none px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none bg-white pr-8"
                                    >
                                        <option value="">בחר מותג</option>
                                        {brands.map(brand => (
                                            <option key={brand.id} value={brand.id}>{brand.name}</option>
                                        ))}
                                    </select>
                                    <ChevronDownIcon className="h-4 w-4 text-[#5c5f62] absolute left-3 top-3" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">תגיות</label>
                                <input
                                    type="text"
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyDown={handleAddTag}
                                    placeholder=""
                                    className="w-full px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                />
                                <div className="flex flex-wrap gap-2 mt-3">
                                    {formData.tags.length > 0 && formData.tags.map(tag => (
                                        <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 bg-[#e3e3e3] text-[#202223] rounded text-[13px]">
                                            {tag}
                                            <button onClick={() => removeTag(tag)} className="hover:text-black pd-0.5">
                                                <XMarkIcon className="h-3 w-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Theme Support / Custom Fields (Perfume) */}
                    <div className="bg-white rounded-lg shadow-[0_0_0_1px_rgba(63,63,68,0.05),0_1px_3px_0_rgba(63,63,68,0.15)] p-5">
                        <h3 className="text-[13px] font-semibold text-[#202223] mb-4">מאפיינים נוספים (בושם)</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">משפחת ריח</label>
                                <input
                                    type="text"
                                    name="fragrance_family"
                                    value={formData.fragrance_family}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-[#898f94] rounded focus:ring-2 focus:ring-[#008060] focus:border-[#008060] outline-none transition-shadow"
                                />
                            </div>
                            <div>
                                <label className="block text-[13px] text-[#202223] mb-1">עוצמה (1-5)</label>
                                <input
                                    type="range"
                                    min="1"
                                    max="5"
                                    name="intensity"
                                    value={formData.intensity}
                                    onChange={handleInputChange}
                                    className="w-full accent-[#008060]"
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Footer / Bottom Actions */}
            <div className="max-w-[1000px] mx-auto px-4 mt-8 flex justify-end gap-3 pb-8 border-t border-[#e1e3e5] pt-4">
                {isDirty && (
                    <div className="flex gap-3">
                        <button className="px-4 py-2 text-[14px] font-medium text-[#202223] border border-[#d2d5d8] rounded hover:bg-[#fafbfb] shadow-sm bg-white">
                            בטל שינויים
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-4 py-2 text-[14px] font-medium text-white bg-[#008060] hover:bg-[#006e52] rounded shadow-sm"
                        >
                            שמור
                        </button>
                    </div>
                )}
                {!isDirty && (
                    <button className="px-4 py-2 text-[14px] font-medium text-[#c9302c] border border-[#d2d5d8] rounded hover:bg-[#fff4f4] shadow-sm bg-white">
                        מחק מוצר
                    </button>
                )}
            </div>

        </div>
    );
};

export default AdminProductEdit;
